/**
 * MONSTER TOWN — KITCHEN & GARDEN ART. COOKING CREW (src/cooking/**).
 *
 * The art builders behind the placeables declared in `src/content/recipes.ts`.
 * They live here so the content file stays a readable list of DATA and both
 * files stay under the 500-line house rule.
 *
 * Every builder obeys the `ArtBuilder` contract: origin at the footprint centre,
 * floor at y = 0, nothing baked in, deterministic, kit geometry and materials
 * only (an older iPad cannot afford a tree that makes its own material).
 */

import * as THREE from 'three'
import type { ArtContext } from '../core/placeable'
import type { PaletteName } from '../core/palette'
import { ball, boxOn, cone, cylinder, group, inked } from '../render/kit'

/**
 * THE COOKING POT — the way into the kitchen.
 *
 * Deliberately NOT another metal box beside the cooker: a kid has to be able to
 * tell "the thing that opens cooking" from "the cooker" at a glance and from
 * across the room, so it is a fat round pot on a chunky wooden block with a
 * spoon standing in it and a board of vegetables beside it. The spoon is the
 * silhouette that reads at 40 px.
 */
export function cookPotArt(ctx: ArtContext): THREE.Object3D {
  const t = ctx.grid.tileSize
  const g = group('cookPot')

  // the block it stands on
  g.add(inked(boxOn(t * 0.92, t * 0.5, t * 0.82, { color: 'woodLight', name: 'bench' })))
  g.add(boxOn(t * 0.96, t * 0.06, t * 0.86, { color: 'wood', y: t * 0.5 }))

  // THE POT IS THE SAME TEAL AS THE POT ON THE COOK SHEET.
  // A kid taps this and a painted screen opens with a big teal pot on it; the
  // two used to be a red pot and a teal pot, which is two objects, not one. The
  // tint is honoured rather than hard-coded, so the palette stays a kid's choice.
  const pot = cylinder(t * 0.3, t * 0.36, { color: ctx.tint ?? 'roofTeal', y: t * 0.56, seg: 14, name: 'pot' })
  g.add(inked(pot))
  g.add(cylinder(t * 0.33, t * 0.06, { color: 'metalDark', y: t * 0.86, seg: 14 }))
  // handles, so it reads as a cooking pot and not as a bucket
  for (const s of [-1, 1]) {
    g.add(
      cylinder(t * 0.05, t * 0.16, {
        color: 'metalDark',
        x: s * t * 0.33,
        y: t * 0.68,
        seg: 6,
      }),
    )
  }
  // what is in it, and the steam coming off it
  g.add(cylinder(t * 0.26, t * 0.04, { color: 'roofMustard', y: t * 0.88, seg: 14, name: 'broth' }))
  const puff = group('steam')
  puff.position.set(0, t * 0.96, 0)
  puff.add(ball(t * 0.07, { color: 'flowerWhite', y: t * 0.1, seg: 6 }))
  puff.add(ball(t * 0.05, { color: 'flowerWhite', x: t * 0.09, y: t * 0.22, seg: 6 }))
  g.add(puff)

  // the spoon — the readable bit
  const spoon = group('spoon')
  spoon.position.set(t * 0.14, t * 0.7, t * 0.06)
  spoon.rotation.z = -0.42
  const stick = cylinder(t * 0.035, t * 0.52, { color: 'woodDark', seg: 6 })
  spoon.add(stick)
  spoon.add(ball(t * 0.09, { color: 'woodLight', y: t * 0.54, seg: 8 }))
  g.add(spoon)

  // a chopping board with something on it
  g.add(boxOn(t * 0.34, t * 0.05, t * 0.24, { color: 'wood', x: -t * 0.28, y: t * 0.56, z: t * 0.2 }))
  g.add(ball(t * 0.06, { color: 'leafLight', x: -t * 0.34, y: t * 0.64, z: t * 0.2, seg: 6 }))
  g.add(ball(t * 0.05, { color: 'flowerRed', x: -t * 0.22, y: t * 0.63, z: t * 0.2, seg: 6 }))
  return g
}

/**
 * A FRUIT TREE YOU CAN PICK.
 *
 * Chunky trunk, three overlapping crown balls (never one sphere — one sphere
 * reads as a lollipop under a fixed iso camera) and six fruit hung on the
 * outside of the crown where a kid can actually see them. `fruit` is named so
 * the tap animation can bob them.
 */
export function fruitTreeArt(ctx: ArtContext, fruitColor: PaletteName): THREE.Object3D {
  const t = ctx.grid.tileSize
  const g = group('fruitTree')
  const v = ctx.variety

  const trunk = cylinder(t * 0.16, t * 0.92, { color: 'trunk', seg: 8, name: 'trunk' })
  g.add(inked(trunk))
  g.add(cylinder(t * 0.22, t * 0.1, { color: 'trunkDark', seg: 8 }))

  const crown = group('crown')
  crown.position.y = t * 0.92
  crown.rotation.y = v * Math.PI
  const blobs: [number, number, number, number, PaletteName][] = [
    [0, t * 0.36, 0, t * 0.52, 'leaf'],
    [-t * 0.3, t * 0.2, t * 0.16, t * 0.36, 'leafLight'],
    [t * 0.28, t * 0.24, -t * 0.14, t * 0.34, 'leafDark'],
  ]
  for (const [x, y, z, r, c] of blobs) {
    crown.add(inked(ball(r, { color: c, x, y, z, seg: 10 })))
  }
  g.add(crown)

  const fruit = group('fruit')
  fruit.position.y = t * 0.92
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2 + v * 2
    fruit.add(
      ball(t * 0.11, {
        color: fruitColor,
        x: Math.cos(a) * t * 0.44,
        y: t * 0.14 + (i % 2) * t * 0.22,
        z: Math.sin(a) * t * 0.44,
        seg: 8,
      }),
    )
  }
  g.add(fruit)
  return g
}

/**
 * A VEGETABLE PATCH — a raised bed of soil with leafy tops poking out.
 *
 * Walkable is false on purpose: it is a thing you tap, and a thing a monster
 * can stand in the middle of is a thing a kid cannot see.
 */
export function vegPatchArt(ctx: ArtContext, leaf: PaletteName, crop: PaletteName): THREE.Object3D {
  const t = ctx.grid.tileSize
  const g = group('vegPatch')

  // the bed
  g.add(inked(boxOn(t * 0.9, t * 0.22, t * 0.9, { color: 'dirtDark', name: 'bed' })))
  g.add(boxOn(t * 0.78, t * 0.06, t * 0.78, { color: 'dirt', y: t * 0.22 }))
  // plank edging, so it reads as a garden bed rather than a mud tile
  for (const [x, z, w, d] of [
    [0, -t * 0.44, t * 0.94, t * 0.1],
    [0, t * 0.44, t * 0.94, t * 0.1],
    [-t * 0.44, 0, t * 0.1, t * 0.94],
    [t * 0.44, 0, t * 0.1, t * 0.94],
  ] as [number, number, number, number][]) {
    g.add(boxOn(w, t * 0.3, d, { color: 'woodLight', x, z }))
  }

  const tops = group('tops')
  tops.position.y = t * 0.28
  let i = 0
  for (const x of [-0.24, 0, 0.24]) {
    for (const z of [-0.24, 0.24]) {
      const sprout = group(`sprout${i}`)
      sprout.position.set(x * t, 0, z * t)
      sprout.rotation.y = ((i * 37) % 360) * (Math.PI / 180)
      sprout.add(cone(t * 0.09, t * 0.2, { color: crop, seg: 7 }))
      sprout.add(ball(t * 0.11, { color: leaf, y: t * 0.26, seg: 7 }))
      sprout.add(ball(t * 0.08, { color: leaf, x: t * 0.09, y: t * 0.2, seg: 6 }))
      tops.add(sprout)
      i++
    }
  }
  g.add(tops)
  return g
}

/**
 * ===========================================================================
 *  THE TWO THINGS THE COOK SHEET DRAWS AND THE REAL ROOM DID NOT HAVE
 * ===========================================================================
 * The critic, on `04-the-kitchen-room.png` beside `07b-cooking-mid-close.png`:
 * *"a kid taps a drab room and gets a beautiful illustration; the two should be
 * recognisably the same kitchen."* The illustration has a window with hills and
 * a sun in it and a shelf of jars with a pan hanging over it. The room a kid
 * walks into had neither.
 *
 * Both are CONTENT, not engine: a placeable in `src/content/recipes.ts` with its
 * art here, seeded onto the kitchen's back wall by `seedGardenAndKitchen()` the
 * same way the pot and the pantry already are. No file outside `src/cooking/**`
 * and `src/content/recipes.ts` changed to put a window in a kid's kitchen, which
 * is the content litmus test working.
 *
 * BOTH HANG ON THE WALL. A 1x1 placeable stands in the middle of its cell, so
 * these are built flat against the BACK of their own footprint (−z, which is
 * north at rot 0) and lifted up the wall. Rotate one and it hangs on whichever
 * wall it is facing.
 */

/**
 * How far back in its own cell a wall-mounted thing sits. Measured, not guessed:
 * a 1 m interior cell puts its north edge half a tile from the centre, so a body
 * 0.09 deep centred at −0.455 has its back face ON the wall at −0.5. The first
 * pass used −0.42 and both props read as pictures hung several centimetres proud
 * of the plaster, which the contact shadow then advertised.
 */
const WALL_Z = -0.455

/**
 * A THING ON A WALL DOES NOT CAST A SHADOW ONTO THE WALL IT IS TOUCHING.
 * With the shadow left on, a flush window still floated: the shadow is what the
 * eye reads as distance. Called at the end of both builders below.
 */
function flatToWall(root: THREE.Object3D): THREE.Object3D {
  root.traverse((o) => {
    const m = o as THREE.Mesh
    if (m.isMesh === true) m.castShadow = false
  })
  return root
}

/** A window on the wall, with the same day outside it that the sheet paints. */
export function kitchenWindowArt(ctx: ArtContext): THREE.Object3D {
  const t = ctx.grid.tileSize
  const g = group('kitchenWindow')
  const z = t * WALL_Z

  // the frame, and the sill it sits on
  g.add(inked(boxOn(t * 0.92, t * 0.86, t * 0.09, { color: 'flowerWhite', y: t * 1.02, z, name: 'frame' })))
  g.add(inked(boxOn(t * 0.98, t * 0.08, t * 0.2, { color: 'woodLight', y: t * 0.96, z: z + t * 0.05 })))

  // the day outside — sky, two hills, a sun. Flat panes, no lighting to fight.
  const panes = group('panes')
  panes.position.set(0, 0, z + t * 0.055)
  panes.add(boxOn(t * 0.78, t * 0.72, t * 0.02, { color: 'glass', y: t * 1.09, flat: true }))
  panes.add(boxOn(t * 0.78, t * 0.2, t * 0.03, { color: 'grassLight', y: t * 1.09, flat: true }))
  panes.add(ball(t * 0.13, { color: 'grassLight', x: -t * 0.18, y: t * 1.29, z: t * 0.01, seg: 8 }))
  panes.add(ball(t * 0.15, { color: 'grassLight', x: t * 0.2, y: t * 1.3, z: t * 0.01, seg: 8 }))
  panes.add(ball(t * 0.1, { color: 'flowerYellow', x: t * 0.24, y: t * 1.62, z: t * 0.02, seg: 8 }))
  g.add(panes)

  // the glazing bars, in front of the day
  g.add(boxOn(t * 0.06, t * 0.78, t * 0.04, { color: 'flowerWhite', y: t * 1.06, z: z + t * 0.075 }))
  g.add(boxOn(t * 0.82, t * 0.06, t * 0.04, { color: 'flowerWhite', y: t * 1.42, z: z + t * 0.075 }))

  // a little pot of herbs on the sill, so the shelf is not the only green
  const herbs = group('herbs')
  herbs.position.set(t * 0.3, t * 1.0, z + t * 0.06)
  herbs.add(cylinder(t * 0.08, t * 0.12, { color: 'roofRed', seg: 8 }))
  herbs.add(ball(t * 0.09, { color: 'leaf', y: t * 0.2, seg: 8 }))
  herbs.add(ball(t * 0.06, { color: 'leafLight', x: t * 0.07, y: t * 0.26, seg: 6 }))
  g.add(herbs)
  return flatToWall(g)
}

/** The shelf of jars with a pan on its rail — straight off the cook sheet. */
export function kitchenShelfArt(ctx: ArtContext): THREE.Object3D {
  const t = ctx.grid.tileSize
  const g = group('kitchenShelf')
  const z = t * WALL_Z

  /*
   * IT STANDS ON THE FLOOR, and that is not only a look.
   *
   * `__harness.tapInstance` — which is how `playthrough` presses every placed
   * thing in the game — aims at the FOOTPRINT CENTRE forty centimetres off the
   * floor. For a plank hung a metre up the wall that point is mid-air in front of
   * the prop, the ray runs on past it, and whatever is next along the same
   * diagonal wins the pick. Measured: hung, this reacted at exactly one of the
   * four camera rotations. A unit whose lower board CONTAINS (0, 0.4, 0) is hit
   * by that ray at every rotation nothing nearer is standing in.
   *
   * It is still the shelf off the cook sheet — the plank, the jars, the pan on
   * its rail — with a second board underneath and two uprights holding it up.
   */
  const back = t * 0.46
  const front = t * 0.05
  const deep = back + front
  const mid = (front - back) / 2

  for (const s of [-1, 1]) {
    g.add(inked(boxOn(t * 0.08, t * 1.0, deep, { color: 'woodDark', x: s * t * 0.42, z: mid, name: 'upright' })))
  }
  // THE BOARD THE TAP LANDS ON. y 0.34–0.42 and the full depth of the unit.
  g.add(inked(boxOn(t * 0.9, t * 0.08, deep, { color: ctx.tint ?? 'woodLight', y: t * 0.34, z: mid, name: 'lower' })))
  // A couple of bowls on it, so it is a shelf with things on rather than a slot.
  g.add(inked(cylinder(t * 0.13, t * 0.1, { color: 'plate', x: -t * 0.2, y: t * 0.42, z: mid, seg: 12 })))
  g.add(cylinder(t * 0.11, t * 0.08, { color: 'iceMint', x: t * 0.16, y: t * 0.42, z: mid, seg: 12 }))

  // the top plank
  g.add(inked(boxOn(t * 0.94, t * 0.08, t * 0.34, { color: ctx.tint ?? 'woodLight', y: t * 0.94, z: z + t * 0.13, name: 'plank' })))

  // jars ON the shelf — the same yellow and lilac pair the cook sheet paints
  const jars = group('jars')
  jars.position.set(0, t * 1.02, z + t * 0.1)
  const pots: [number, PaletteName, PaletteName, number][] = [
    [-0.3, 'flowerYellow', 'roofMustard', 0.2],
    [-0.04, 'flowerPurple', 'wallLilac', 0.24],
    [0.24, 'iceMint', 'metalDark', 0.17],
  ]
  for (const [x, body, lid, h] of pots) {
    jars.add(cylinder(t * 0.1, t * h, { color: body, x: x * t, seg: 10 }))
    jars.add(cylinder(t * 0.11, t * 0.05, { color: lid, x: x * t, y: t * h, seg: 10 }))
  }
  g.add(jars)

  // the rail above, and the pan hanging off it. The rail is a box, not a
  // cylinder: `cylinder()` only ever stands up the Y axis.
  g.add(boxOn(t * 0.9, t * 0.05, t * 0.05, { color: 'metalDark', y: t * 1.64, z: z + t * 0.08 }))
  const pan = group('pan')
  pan.position.set(-t * 0.14, t * 1.36, z + t * 0.12)
  // A disc facing the room, so the pan reads as a pan and not as a tin lid seen
  // edge-on. `cylinder()` has no rotX, so the mesh is turned after it is built.
  const face = cylinder(t * 0.2, t * 0.06, { color: 'metal', seg: 12 })
  face.rotation.x = Math.PI / 2
  pan.add(inked(face))
  pan.add(boxOn(t * 0.3, t * 0.05, t * 0.05, { color: 'woodDark', x: t * 0.34, y: -t * 0.02 }))
  pan.add(cylinder(t * 0.02, t * 0.16, { color: 'metalDark', x: -t * 0.02, y: t * 0.16, seg: 6 }))
  g.add(pan)
  // Shadows stay ON here — this one is standing on the floor, and a floor-
  // standing thing with no contact shadow is the thing that floats.
  return g
}

/**
 * THE PANTRY — a small dresser with jars on it, for the store-cupboard
 * ingredients (grain, milk, honey) that no garden grows.
 */
export function pantryArt(ctx: ArtContext): THREE.Object3D {
  const t = ctx.grid.tileSize
  const g = group('pantry')

  // A WOODEN DRESSER, not a beige block. It stood in a mint room in `wallCream`
  // next to the interiors crew's cream counters, and the critic read the whole
  // back wall as "a stack of mismatched beige counter blocks". Wood with cream
  // doors is the same pair of colours as the shelf on the cook sheet.
  g.add(inked(boxOn(t * 0.86, t * 1.18, t * 0.5, { color: ctx.tint ?? 'woodLight', name: 'body' })))
  g.add(boxOn(t * 0.9, t * 0.08, t * 0.56, { color: 'woodDark', y: t * 1.18 }))
  // two doors with knobs
  for (const s of [-1, 1]) {
    g.add(
      boxOn(t * 0.36, t * 0.5, t * 0.06, {
        color: 'wallCream',
        x: s * t * 0.21,
        y: t * 0.1,
        z: t * 0.26,
        name: s < 0 ? 'doorL' : 'doorR',
      }),
    )
    g.add(ball(t * 0.045, { color: 'metalDark', x: s * t * 0.06, y: t * 0.35, z: t * 0.31, seg: 6 }))
  }
  // an open shelf of jars — this is what says "food lives in here"
  g.add(boxOn(t * 0.78, t * 0.05, t * 0.44, { color: 'woodLight', y: t * 0.72 }))
  const jars = group('jars')
  jars.position.y = t * 0.77
  const cols: PaletteName[] = ['roofMustard', 'flowerRed', 'iceMint', 'wallLilac']
  cols.forEach((c, i) => {
    const x = (i - 1.5) * t * 0.19
    jars.add(cylinder(t * 0.07, t * 0.2, { color: c, x, seg: 8 }))
    jars.add(cylinder(t * 0.075, t * 0.04, { color: 'metalDark', x, y: t * 0.2, seg: 8 }))
  })
  g.add(jars)
  return g
}
